import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { apiFetch } from '../../services/api';
import { useBooking } from '../../context/BookingContext';
import axios from 'axios';

// Flight search results interface matching our travel.js backend LetSFG mapper
interface FlightResult {
  id: string;
  airline: string;
  airlineCode?: string;
  logo: string;
  origin: string;
  destination: string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  stops: number;
  retailPrice: number; // NGN
  rawPriceUSD: number; // USD
  currency: string;
  cabin_class: string;
  search_id: string;
  offer_id: string;
  booking_url?: string;
}

interface HotelResult {
  id: string;
  name: string;
  stars: number;
  image: string;
  address: string;
  retailPrice: number;
  rawPriceUSD: number;
  currency: string;
  board: string;
  session_id: string;
}

const formatPrice = (n: number) => `₦${n.toLocaleString()}`;

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart } = useBooking();
  const searchType = searchParams.get('type') || 'flights';
  
  const [loading, setLoading] = useState(true);
  const [flightResults, setFlightResults] = useState<FlightResult[]>([]);
  const [hotelResults, setHotelResults] = useState<HotelResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Search Param extraction
  const origin = searchParams.get('from') || searchParams.get('origin') || 'LOS';
  const destination = searchParams.get('to') || searchParams.get('destination') || 'LHR';
  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const returnDate = searchParams.get('return_date') || searchParams.get('returnDate');
  const passengers = parseInt(searchParams.get('adults') || searchParams.get('guests') || '1');
  const cabinClass = searchParams.get('cabin_class') || searchParams.get('class') || 'economy';
  
  const hotelDest = searchParams.get('destination') || searchParams.get('to') || 'London';
  const checkIn = searchParams.get('checkIn') || new Date().toISOString().split('T')[0];
  const checkOut = searchParams.get('checkOut');

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (searchType === 'flights') {
          const payload = {
            origin,
            destination,
            date,
            return_date: returnDate,
            passengers,
            cabin_class: cabinClass
          };

          const response = await apiFetch('/travel/search-flights', {
            method: 'POST',
            body: JSON.stringify(payload)
          });

          setFlightResults(response.results || []);
        } else if (searchType === 'hotels') {
          const cityIdParam = searchParams.get('city_id');
          const payload = {
            destination: hotelDest,
            city_name: hotelDest,
            city_id: cityIdParam ? parseInt(cityIdParam, 10) : undefined,
            checkIn,
            checkOut,
            guests: passengers
          };

          const response = await apiFetch('/travel/search-hotels', {
            method: 'POST',
            body: JSON.stringify(payload)
          });

          setHotelResults(response.results || []);
        }
      } catch (err: any) {
        console.error('Search error:', err);
        setError(err.message || 'Failed to fetch search results.');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [searchParams, searchType]);

  const handleBookFlight = (flight: FlightResult) => {
    addToCart({
      type: 'flight',
      price: flight.retailPrice,
      rawPriceUSD: flight.rawPriceUSD,
      search_id: flight.search_id,
      offer_id: flight.offer_id || flight.id,
      details: {
        airline: flight.airline,
        origin: flight.origin,
        destination: flight.destination,
        departureTime: flight.departureTime,
        arrivalTime: flight.arrivalTime,
        duration: flight.duration,
        stops: flight.stops,
        cabin_class: flight.cabin_class,
        booking_url: flight.booking_url,
        return_date: returnDate
      }
    });
    navigate('/booking/checkout');
  };

  const handleBookHotel = (hotel: HotelResult) => {
    const effectiveSessionId = hotel.session_id || (hotel as any).search_id || '';
    const hotelCode = (hotel as any).hotel_code || hotel.id;
    const combinationId = (hotel as any).combination_id_v2 || (hotel as any).offer_id || (hotel as any).offers?.[0]?.id || hotelCode;
    const expectedPrice = (hotel as any).expected_price !== undefined ? (hotel as any).expected_price : (hotel.rawPriceUSD || 0);
    const expectedCost = (hotel as any).expected_cost !== undefined ? (hotel as any).expected_cost : expectedPrice;
    const cityId = (hotel as any).city_id || 9821;
    const cityName = (hotel as any).city_name || (hotel as any).city || hotelDest || 'Lagos';
    const guestCount = passengers || 1;

    addToCart({
      type: 'hotel',
      price: hotel.retailPrice,
      rawPriceUSD: hotel.rawPriceUSD,
      search_id: effectiveSessionId,
      offer_id: String(combinationId),
      session_id: effectiveSessionId,
      details: {
        name: hotel.name,
        address: hotel.address,
        stars: hotel.stars,
        board: hotel.board,
        image: hotel.image,
        hotel_code: hotelCode,
        combination_id_v2: String(combinationId),
        offer_id: String(combinationId),
        session_id: effectiveSessionId,
        expected_price: expectedPrice,
        expected_cost: expectedCost,
        currency: (hotel as any).offer_currency || 'USD',
        fx_rate: (hotel as any).fx_rate || 1,
        raw_offer: (hotel as any).raw_offer,
        city_id: cityId,
        city_name: cityName,
        check_in: checkIn,
        check_out: checkOut || new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
        guests: guestCount,
        adults: guestCount,
        checkIn,
        checkOut: checkOut || new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0]
      }
    });
    navigate('/booking/checkout');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Header />
      
      {/* Premium Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[70%] bg-logo-gold-500/10 blur-[120px] rounded-full"></div>
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[70%] bg-emerald-500/10 blur-[120px] rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            {searchType === 'flights' ? 'Select Your Flight' : 'Select Your Hotel'}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
            {searchType === 'flights' 
              ? `Showing results for ${origin} to ${destination} on ${new Date(date).toLocaleDateString('en-GB')} ${returnDate ? `returning ${new Date(returnDate).toLocaleDateString('en-GB')}` : ''}`
              : `Showing hotels in ${hotelDest} from ${new Date(checkIn).toLocaleDateString('en-GB')}`}
          </p>
          
          <button 
            onClick={() => navigate('/general')} 
            className="text-logo-gold-400 hover:text-logo-gold-300 font-medium flex items-center justify-center gap-2 mx-auto"
          >
            <i className="ri-arrow-left-line"></i> Modify Search
          </button>
        </div>
      </section>

      {/* Results Section */}
      <main className="flex-grow py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full -mt-10 relative z-20">
        
        {loading ? (
          <div className="bg-white rounded-2xl shadow-xl p-16 text-center border border-slate-100 flex flex-col items-center justify-center space-y-6">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-logo-gold-500 rounded-full border-t-transparent animate-spin"></div>
              <i className={`${searchType === 'flights' ? 'ri-plane-fill' : 'ri-hotel-fill'} absolute inset-0 flex items-center justify-center text-2xl text-navy-900`}></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-navy-900 mb-2">Searching live inventory...</h3>
              <p className="text-slate-500">Contacting {searchType === 'flights' ? 'airlines' : 'hotels'} globally for the best available prices.</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 rounded-2xl shadow-sm border border-red-100 p-8 text-center">
            <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
            <h3 className="text-xl font-bold text-red-900 mb-2">Search Failed</h3>
            <p className="text-red-700">{error}</p>
            <button onClick={() => navigate('/general')} className="mt-6 bg-red-600 text-white px-6 py-2 rounded-xl font-medium hover:bg-red-700">Try Again</button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Flight Results */}
            {searchType === 'flights' && (
              flightResults.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
                  <i className="ri-flight-takeoff-line text-5xl text-slate-300 mb-4"></i>
                  <h3 className="text-xl font-bold text-navy-900 mb-2">No flights found</h3>
                  <p className="text-slate-500 mb-6">We couldn't find any flights matching your criteria. Try adjusting your dates or destinations.</p>
                  <button onClick={() => navigate('/general?tab=flights')} className="bg-navy-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-navy-800">Modify Search</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-navy-900">{flightResults.length} Flights Available</h2>
                    <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 text-sm font-medium text-slate-600 flex items-center gap-2">
                      <i className="ri-sort-desc"></i> Sorted by lowest price
                    </div>
                  </div>
                  {flightResults.map((flight) => (
                    <div key={flight.id} className="bg-white rounded-2xl shadow-sm hover:shadow-md border border-slate-100 p-6 transition-all flex flex-col md:flex-row items-center gap-6 group">
                      <div className="flex-shrink-0 bg-slate-50 p-4 rounded-xl border border-slate-100">
                        {flight.logo ? (
                          <img src={flight.logo} alt={flight.airline} className="w-16 h-16 object-contain mix-blend-multiply" />
                        ) : (
                          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                            <i className="ri-plane-fill text-3xl text-logo-gold-500"></i>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-grow grid grid-cols-1 md:grid-cols-3 gap-6 w-full items-center text-center md:text-left border-b md:border-b-0 md:border-r border-slate-100 pb-6 md:pb-0 md:pr-6">
                        <div>
                          <p className="text-2xl font-bold text-navy-900">{flight.departureTime}</p>
                          <p className="text-sm font-medium text-slate-500">{flight.origin}</p>
                        </div>
                        <div className="flex flex-col items-center justify-center px-4">
                          <p className="text-xs font-semibold text-slate-400 mb-1">{flight.duration}</p>
                          <div className="w-full flex items-center justify-center gap-2">
                            <div className="h-[2px] bg-slate-200 flex-grow relative">
                              <div className="absolute inset-0 bg-logo-gold-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                            </div>
                            <i className="ri-plane-fill text-logo-gold-500 rotate-90"></i>
                          </div>
                          <p className="text-xs font-semibold text-logo-gold-600 mt-1">
                            {flight.stops === 0 ? 'Direct' : `${flight.stops} Stop${flight.stops > 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <div className="md:text-right">
                          <p className="text-2xl font-bold text-navy-900">{flight.arrivalTime}</p>
                          <p className="text-sm font-medium text-slate-500">{flight.destination}</p>
                        </div>
                      </div>

                      <div className="flex flex-col items-center md:items-end justify-center gap-2 min-w-[150px]">
                        <p className="text-2xl font-extrabold text-navy-900">{formatPrice(flight.retailPrice)}</p>
                        <p className="text-xs text-slate-400 mb-2 capitalize">{flight.cabin_class}</p>
                        <button
                          className="w-full bg-navy-900 hover:bg-logo-gold-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-sm shadow-md"
                          onClick={() => handleBookFlight(flight)}
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Hotel Results */}
            {searchType === 'hotels' && (
              hotelResults.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
                  <i className="ri-building-4-line text-5xl text-slate-300 mb-4"></i>
                  <h3 className="text-xl font-bold text-navy-900 mb-2">No hotels found</h3>
                  <p className="text-slate-500 mb-6">We couldn't find any hotels matching your criteria in {hotelDest}.</p>
                  <button onClick={() => navigate('/general?tab=hotels')} className="bg-navy-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-navy-800">Modify Search</button>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-navy-900">{hotelResults.length} Hotels Available</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {hotelResults.map((hotel) => (
                      <div key={hotel.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-slate-100 overflow-hidden transition-all group flex flex-col">
                        <div className="relative h-56 overflow-hidden">
                          <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full text-sm font-bold text-navy-900 flex items-center gap-1 shadow-sm">
                            <i className="ri-star-fill text-logo-gold-500"></i> {hotel.stars}
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                        
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="text-lg font-bold text-navy-900 mb-2 line-clamp-2">{hotel.name}</h3>
                          <p className="text-sm text-slate-500 flex items-start gap-1 mb-4 line-clamp-2">
                            <i className="ri-map-pin-line text-logo-gold-500 mt-0.5"></i>
                            {hotel.address}
                          </p>
                          
                          <div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between">
                            <div>
                              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">{hotel.board || 'Room Only'}</p>
                              <p className="text-2xl font-extrabold text-navy-900">{formatPrice(hotel.retailPrice)}</p>
                              <p className="text-[10px] text-slate-400">per night</p>
                            </div>
                            <button
                              className="bg-navy-900 hover:bg-logo-gold-600 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors shadow-md"
                              onClick={() => handleBookHotel(hotel)}
                            >
                              Book Now
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
